import React, { useMemo, useState } from 'react';

import ChevronRightRoundedIcon from '@material-ui/icons/ChevronRightRounded';
import {
  disableActionItemEditingAction,
  enableActionItemEditingAction,
  removeActionItemAction,
} from 'actions/actionItem';
import EmployeeOwnerAvatar from 'components/EmployeeOwnerAvatar';
import EmployeesEdit from 'components/EmployeesEdit';
import { EmptyNodeView } from 'components/NodesView';
import PostField from 'components/feed/PostField';
import TextParseComponent from 'components/feed/TextParseComponent';
import { useOwnersState } from 'components/hooks/employee';
import SimplePopup from 'components/popup/SimplePopup';
import { ActionItem, OwnerCollaboratorEmployee } from 'models/actionItem';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AgendaTag from 'screens/common/sidepanel/Agenda/AgendaTag';
import DueDateField from 'screens/common/sidepanel/DetailSection/DueDateField';
import { StyledNodesView, SubTaskCheck } from 'screens/common/sidepanel/sidepanelStyles';
import styled, { css } from 'styled-components';
import {
  NewStyledIconButton,
  StyledTypography,
  editorStyles,
  editorPlaceholderStyles,
  StrikethroughCSS1,
  VerticallyCenterContainer,
} from 'styles/common';

import { CREATE_TASK_PLACEHOLDER, SEARCH_WIDGET_TYPES, TASK_TYPES, TASK_TYPES_2 } from 'constants/ProjectConstants';
import { UncheckedCircle, StyledIcon } from 'constants/icons';
import { sendAddedActionItemDate } from 'constants/mixpanelHelper';

import { convertTextMacrosToEditorState } from 'services/helpers/post';
import { sortToIdsOwnersFirstAscendingName } from 'services/helpers/task';
import { showTaskDetails } from 'services/taskDetails';

const whiteBackgroundInputStyles = css`
  border: 1px solid #eee;
  border-radius: 4px;
  padding: calc(0.25rem - 1px) calc(0.5rem - 1px);
  background: #ffffffdb;
`;

const currentPostFieldPlaceholderStyles = css`
  ${editorPlaceholderStyles};
  top: 0.25rem;
  left: 0.5rem;
  font-size: ${(props) => props.theme.typography.body2.fontSize};
`;

const whiteBackgroundEditorStyles = css`
  ${editorStyles};
  ${whiteBackgroundInputStyles};
  font-size: ${(props) => props.theme.typography.body2.fontSize};
  line-height: ${(props) => props.theme.typography.body2.lineHeight};
`;

const GoToIconButton = styled(NewStyledIconButton)`
  visibility: hidden;
`;

const TaskNameTypography = styled(StyledTypography)`
  ${StrikethroughCSS1};
  padding-right: 0.5rem;
  padding-left: 0.5rem;
  // adding transparent styles to avoid shifting issue
  border: 1px solid transparent;
  border-radius: 4px;
  padding: calc(0.25rem - 1px) calc(0.5rem - 1px);
  background: transparent;
`;

const Container = styled.div`
  display: flex;
  margin: 0 -1rem 0 -1.5rem;
  padding: 0.25rem 0.5rem 0.25rem 2em;
  justify-content: space-between;
  // min-height: calc(2rem + ${(props) => props.theme.typography.body2.lineHeight}rem);
  ${(props) =>
    props.isEditing &&
    css`
      background-color: ${(props) => props.theme.palette.background.lighterBlue};
    `}
  &:hover {
    // padding: 0.25rem 0 0.25rem 0;
    background-color: ${(props) => props.theme.palette.background.lighterBlue};
    ${GoToIconButton} {
      visibility: visible;
    }
    ${TaskNameTypography} {
      display: inline-block;
      min-width: 60%;
      margin-left: 0;
      cursor: text;
      ${whiteBackgroundInputStyles};
      min-height: 60%;
    }
  }
`;

const LeftChildContainer = styled.div`
  flex: 1 1 auto;
  // flex-grow: 1;
  padding-right: 1rem;
  display: flex;
  align-items: center;
`;

const RightChildContainer = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const StyledEmptyNodeView = styled(EmptyNodeView)`
  position: relative;
  box-sizing: content-box;
`;

const StyledPostField = styled((props) => <PostField {...props} />)`
  width: 100%;
`;

const LeftAlignedSimpleOwnerPopup = styled(SimplePopup)`
  left: -400%;
`;

const StyledEmployeesEdit = styled(EmployeesEdit)`
  padding: 1rem;
  background: white;
  box-shadow: ${(props) => props.theme.shadows[1]};
`;

export default function ActionItemDisplay({
  actionItem,
  onCheckToggle,
  onUpdate,
  actionItemsDispatch,
  filterEmpIds = [],
  isDisabled = false,
}) {
  const history = useHistory();
  const dispatch = useDispatch();

  const { text, toIds } = actionItem;

  const [currentEditorState, setCurrentEditorState] = useState(convertTextMacrosToEditorState(text, {}));

  const [ownerPopupAnchor, setownerPopupAnchor] = useState(null);

  const { currentOwners } = useOwnersState(toIds);

  const sortedCurrentOwners = useMemo(() => {
    if (Array.isArray(currentOwners)) {
      const mappedCurrentOwners = currentOwners.map((emp) => ({
        ...emp,
        id: emp.id,
        fullName: emp.displayName,
        image: emp.profilePhotoSrc,
      }));
      return sortToIdsOwnersFirstAscendingName(mappedCurrentOwners);
    }
    return [];
  }, [currentOwners]);

  function onToIdsUpdate(employees) {
    const mappedEmployees = employees.map((emp) =>
      OwnerCollaboratorEmployee.fromJSON({
        id: String(emp.id),
        displayName: emp?.displayName || emp?.fullName,
        designation: emp.designation,
        profilePhotoSrc: emp.profilePhotoSrc,
        ownerType: emp.ownerType,
      })
    );

    onUpdate({ ...actionItem, toIds: mappedEmployees, overrideToIds: false });
  }

  const handleUpdate = () => {
    onUpdate({
      ...actionItem,
      text: currentEditorState.getCurrentContent().getPlainText().trim(),
      isEditing: false,
    });
  };

  const handleCancel = () => {
    setCurrentEditorState(convertTextMacrosToEditorState(actionItem.text, {}));
    const action = actionItem.uuid
      ? disableActionItemEditingAction.bind(null, {
          actionItemId: actionItem.uuid,
          key: actionItem.uiKey,
        })
      : removeActionItemAction.bind(null, actionItem.uuid, actionItem.uiKey);
    actionItemsDispatch(action());
  };

  const enableEditing = (ev) => {
    ev.stopPropagation();
    actionItemsDispatch(
      enableActionItemEditingAction({
        actionItemId: actionItem.uuid,
        uiKey: actionItem.uiKey,
      })
    );
  };

  const handleUpdateFromSidePanel = (updatedActionItem) => {
    const { text, status, toIds, coloredDueDate } = updatedActionItem;
    const mappedActionItem = ActionItem.fromJSON({
      uuid: actionItem.uuid,
      text,
      status,
      uiKey: actionItem.uiKey,
      toIds: toIds.map((emp) => {
        const { id, designation, displayName, profilePhotoSrc: profilePhoto, ownerType } = emp;
        return {
          id,
          designation,
          displayName,
          profilePhoto,
          ownerType,
        };
      }),
      dueTime: coloredDueDate,
      label: null,
      teams: [],
    });
    mappedActionItem.teams = actionItem.teams;
    mappedActionItem.label = actionItem.label;
    onUpdate(mappedActionItem);
  };

  const handleActionItemDelete = () => {
    actionItemsDispatch(removeActionItemAction(actionItem.uuid, actionItem.uiKey));
  };

  const coloredDueDate = actionItem.dueTime || {};

  return (
    <Container isEditing={actionItem.isEditing}>
      <LeftChildContainer>
        <SubTaskCheck
          onClick={() => onCheckToggle(actionItem)}
          disableRipple
          checked={actionItem.status === TASK_TYPES.completed}
          checkedIcon={<UncheckedCircle size="header" color={actionItem?.dueTime?.color || 'textPrimary'} />}
          size="h6"
          icon={<UncheckedCircle size="header" color="textSecondary" />}
          disabled={isDisabled}
        />
        <VerticallyCenterContainer style={{ flexGrow: 1, justifyContent: 'space-between' }}>
          {actionItem.isEditing ? (
            <StyledPostField
              placeholder={CREATE_TASK_PLACEHOLDER}
              editorState={currentEditorState}
              updateEditorState={setCurrentEditorState}
              editorStyles={whiteBackgroundEditorStyles}
              useHashtagFunctionality={false}
              useMentionFunctionality={false}
              placeholderStyles={currentPostFieldPlaceholderStyles}
              handleBlur={handleUpdate}
              onEnter={handleUpdate}
              onEscape={handleCancel}
              widgetType={SEARCH_WIDGET_TYPES.task}
              mentionEmpIds={filterEmpIds}
            />
          ) : (
            <TaskNameTypography
              variant="body2"
              display="inline"
              completed={actionItem.status === 'COMPLETED'}
              textDecorationColor={actionItem?.dueTime?.color || 'textPrimary'}
              onClick={isDisabled ? () => {} : enableEditing}
            >
              <TextParseComponent text={actionItem.text} res={{}} />
            </TaskNameTypography>
          )}
          {actionItem?.label ? (
            <span style={{ marginLeft: '1rem' }}>
              <AgendaTag uuid={actionItem?.label?.uuid} labelType={actionItem?.label?.labelType} />
            </span>
          ) : null}
        </VerticallyCenterContainer>
      </LeftChildContainer>
      <RightChildContainer>
        <StyledNodesView
          style={{ marginRight: '1.5rem' }}
          useEmployeeClick={false}
          onClick={(e) => {
            e.preventDefault();
            if (isDisabled) {
              return;
            }

            if (ownerPopupAnchor) {
              setownerPopupAnchor(null);
              return;
            }
            const nodesView = e.currentTarget?.closest('.nodesView');
            setownerPopupAnchor(nodesView || e.currentTarget);
          }}
          employees={sortedCurrentOwners}
          maxNodes={2}
          renderEmptyView={({ onClick }) => (
            <StyledEmptyNodeView onClick={onClick} size="header" leftGutter={-1} tooltip="Add Action Item Owner" />
          )}
          renderAvatar={(props) => (
            <EmployeeOwnerAvatar {...props} size="tinier" leftGutter={-1} border="2px solid #EAEAEA" />
          )}
          renderNodesEditView={() => (
            <LeftAlignedSimpleOwnerPopup
              id={`${actionItem.uuid}-popper`}
              anchorEl={ownerPopupAnchor}
              setAnchor={setownerPopupAnchor}
            >
              <StyledEmployeesEdit employees={sortedCurrentOwners} onUpdate={onToIdsUpdate}>
                <StyledTypography color="textSecondary" variant="body2">
                  Owners
                </StyledTypography>
                <StyledEmployeesEdit.OwnersEdit filterEmpIds={filterEmpIds} />
                <StyledTypography topGutter={1} color="textSecondary" variant="body2">
                  Collaborators
                </StyledTypography>
                <StyledEmployeesEdit.CollaboratorsEdit filterEmpIds={filterEmpIds} />
              </StyledEmployeesEdit>
            </LeftAlignedSimpleOwnerPopup>
          )}
        />
        <DueDateField
          dueDate={coloredDueDate.timeStamp ? new Date(coloredDueDate.timeStamp) : null}
          color={coloredDueDate.color}
          tooltipText={coloredDueDate.reason}
          isCrossed={coloredDueDate.isCrossed}
          updateDueDate={(date) => {
            onUpdate({
              ...actionItem,
              dueTime: {
                ...coloredDueDate,
                timeStamp: date ? date.getTime() : null,
              },
            });
            sendAddedActionItemDate();
          }}
          isDisabled={isDisabled}
        />
        <GoToIconButton
          padding="small"
          onClick={(e) => {
            if (!actionItem.uuid) return;

            dispatch(
              showTaskDetails({
                taskIds: [actionItem.uuid],
                onDelete: handleActionItemDelete,
                employee: {},
                onUpdate: (task) => {
                  if (task) {
                    handleUpdateFromSidePanel({ ...task });
                  }
                },
                type: TASK_TYPES_2.task,
              })
            );
          }}
        >
          <StyledIcon size="smallest" color="textSecondary">
            <ChevronRightRoundedIcon />
          </StyledIcon>
        </GoToIconButton>
      </RightChildContainer>
    </Container>
  );
}
